import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import PriceTargetModal from './PriceTargetModal';
import './PriceTargetsList.css';

const PriceTargetsList = ({ tokenAddress = null, showCreateButton = true }) => {
    const { user } = useAuth();
    const { showToast } = useToast();

    const [priceTargets, setPriceTargets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [filter, setFilter] = useState('active');
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        if (user) {
            fetchPriceTargets();
            fetchStats();
        }
    }, [user, filter, tokenAddress]);

    const fetchPriceTargets = async () => {
        if (!user) return;

        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filter !== 'all') params.append('status', filter);
            if (tokenAddress) params.append('tokenAddress', tokenAddress);

            const tokens = JSON.parse(localStorage.getItem('tokens') || '{}');
            const response = await fetch(`/api/price-targets?${params.toString()}`, {
                headers: {
                    'Authorization': `Bearer ${tokens.access_token}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const result = await response.json();
            if (result.success) {
                setPriceTargets(result.data || []);
            } else {
                setPriceTargets([]);
                const errorMessage = typeof result.error === 'string' ? result.error : 'Failed to fetch price targets';
                showToast(errorMessage, 'error');
            }
        } catch (error) {
            console.error('Error fetching price targets:', error);
            setPriceTargets([]);
            showToast('Failed to fetch price targets', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        if (!user) return;

        try {
            const tokens = JSON.parse(localStorage.getItem('tokens') || '{}');
            const response = await fetch('/api/price-targets/stats', {
                headers: {
                    'Authorization': `Bearer ${tokens.access_token}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const result = await response.json();
            if (result.success) {
                setStats(result.data);
            } else {
                setStats(null);
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
            setStats(null);
        }
    };

    const handleCancelTarget = async (targetId) => {
        if (!window.confirm('Are you sure you want to cancel this price target?')) {
            return;
        }

        try {
            const tokens = JSON.parse(localStorage.getItem('tokens') || '{}');
            const response = await fetch(`/api/price-targets/${targetId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${tokens.access_token}`
                }
            });

            const result = await response.json();
            if (result.success) {
                showToast('Price target cancelled successfully', 'success');
                fetchPriceTargets();
                fetchStats();
            } else {
                showToast(result.error || 'Failed to cancel price target', 'error');
            }
        } catch (error) {
            console.error('Error cancelling price target:', error);
            showToast('Failed to cancel price target', 'error');
        }
    };

    const handleTargetCreated = () => {
        fetchPriceTargets();
        fetchStats();
    };

    const formatPrice = (price) => {
        const num = parseFloat(price);
        if (num < 0.001) {
            return num.toFixed(8);
        } else if (num < 1) {
            return num.toFixed(6);
        } else {
            return num.toFixed(4);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'active': return 'var(--accent-color)';
            case 'executed': return 'var(--success-color)';
            case 'cancelled': return 'var(--text-secondary)';
            case 'expired': return 'var(--warning-color)';
            default: return 'var(--text-secondary)';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'active': return '🎯';
            case 'executed': return '✅';
            case 'cancelled': return '❌';
            case 'expired': return '⏰';
            default: return '❓';
        }
    };

    if (!user) {
        return (
            <div className="price-targets-list">
                <div className="auth-required">
                    <p>Please log in to view your price targets</p>
                </div>
            </div>
        );
    }

    return (
        <div className="price-targets-list">
            <div className="targets-header">
                <div className="header-content">
                    <h3>Price Targets</h3>
                    {showCreateButton && (
                        <button
                            className="create-target-btn"
                            onClick={() => setShowModal(true)}
                        >
                            + New Target
                        </button>
                    )}
                </div>

                {stats && (
                    <div className="targets-stats">
                        <div className="stat">
                            <span className="stat-value">{stats.active_targets}</span>
                            <span className="stat-label">Active</span>
                        </div>
                        <div className="stat">
                            <span className="stat-value">{stats.executed_targets}</span>
                            <span className="stat-label">Executed</span>
                        </div>
                        <div className="stat">
                            <span className="stat-value">{stats.total_targets}</span>
                            <span className="stat-label">Total</span>
                        </div>
                    </div>
                )}

                <div className="filter-tabs">
                    <button
                        className={`filter-tab ${filter === 'active' ? 'active' : ''}`}
                        onClick={() => setFilter('active')}
                    >
                        Active ({stats?.active_targets || 0})
                    </button>
                    <button
                        className={`filter-tab ${filter === 'executed' ? 'active' : ''}`}
                        onClick={() => setFilter('executed')}
                    >
                        Executed ({stats?.executed_targets || 0})
                    </button>
                    <button
                        className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
                        onClick={() => setFilter('all')}
                    >
                        All ({stats?.total_targets || 0})
                    </button>
                </div>
            </div>

            <div className="targets-content">
                {loading ? (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Loading price targets...</p>
                    </div>
                ) : priceTargets.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-icon">🎯</div>
                        <h4>No price targets found</h4>
                        <p>
                            {filter === 'active' 
                                ? 'Create your first price target to get started'
                                : `No ${filter} price targets found`
                            }
                        </p>
                        {showCreateButton && filter === 'active' && (
                            <button
                                className="create-target-btn primary"
                                onClick={() => setShowModal(true)}
                            >
                                Create Price Target
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="targets-grid">
                        {priceTargets.map(target => (
                            <div key={target.id} className="target-card">
                                <div className="target-header">
                                    <div className="target-type">
                                        <span className={`type-badge ${target.target_type}`}>
                                            {target.target_type === 'buy' ? '📈' : '📉'} 
                                            {target.target_type.toUpperCase()}
                                        </span>
                                        <span 
                                            className="status-badge"
                                            style={{ color: getStatusColor(target.status) }}
                                        >
                                            {getStatusIcon(target.status)} {target.status}
                                        </span>
                                    </div>
                                    <div className="target-priority">
                                        Priority: {target.priority}
                                    </div>
                                </div>

                                <div className="target-details">
                                    <div className="token-info">
                                        <span className="token-address">
                                            {target.token_address.slice(0, 8)}...{target.token_address.slice(-8)}
                                        </span>
                                        <span className="wallet-name">
                                            Wallet: {target.wallet_name}
                                        </span>
                                    </div>

                                    <div className="price-info">
                                        <div className="price-row">
                                            <span className="label">Target Price:</span>
                                            <span className="value">${formatPrice(target.target_price)}</span>
                                        </div>
                                        <div className="price-row">
                                            <span className="label">Amount:</span>
                                            <span className="value">
                                                {formatPrice(target.amount)} {target.amount_type.toUpperCase()}
                                            </span>
                                        </div>
                                        {target.execution_price && (
                                            <div className="price-row">
                                                <span className="label">Executed at:</span>
                                                <span className="value">${formatPrice(target.execution_price)}</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="target-metadata">
                                        <div className="metadata-row">
                                            <span className="label">Created:</span>
                                            <span className="value">{formatDate(target.created_at)}</span>
                                        </div>
                                        {target.expires_at && (
                                            <div className="metadata-row">
                                                <span className="label">Expires:</span>
                                                <span className="value">{formatDate(target.expires_at)}</span>
                                            </div>
                                        )}
                                        {target.executed_at && (
                                            <div className="metadata-row">
                                                <span className="label">Executed:</span>
                                                <span className="value">{formatDate(target.executed_at)}</span>
                                            </div>
                                        )}
                                    </div>

                                    {target.notes && (
                                        <div className="target-notes">
                                            <span className="notes-label">Notes:</span>
                                            <p className="notes-text">{target.notes}</p>
                                        </div>
                                    )}
                                </div>

                                {target.status === 'active' && (
                                    <div className="target-actions">
                                        <button
                                            className="cancel-btn"
                                            onClick={() => handleCancelTarget(target.id)}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                )}

                                {target.transaction_hash && (
                                    <div className="transaction-link">
                                        <a
                                            href={`https://solscan.io/tx/${target.transaction_hash}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="tx-link"
                                        >
                                            View Transaction ↗
                                        </a>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <PriceTargetModal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                token={null}
                onTargetCreated={handleTargetCreated}
            />
        </div>
    );
};

export default PriceTargetsList;